import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';

/**
 * Hilfsfunktion um RGB/RGBA-Farben in Hex-Format umzuwandeln
 */
const rgbaToHex = (color) => {
  if (!color || typeof color !== 'string') return '#000000';
  
  // Wenn bereits ein Hex-Wert, gib ihn zurück
  if (color.startsWith('#')) {
    return color;
  }
  
  // RGB/RGBA-Werte extrahieren
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    
    // In Hex umwandeln
    const toHex = (n) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  
  // Fallback
  return '#000000';
};

/**
 * Hilfsfunktion um Datum in YYYY-MM-DD Format umzuwandeln
 */
const formatDateForInput = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Überprüft, ob alle Tasks den gleichen Wert für eine Eigenschaft haben
 */
const hasUniformValues = (tasks, property) => {
  if (tasks.length <= 1) return true;
  
  // Mapping der UI-Properties auf die tatsächlichen Task-Properties
  const propertyMapping = {
    'label': 'label',
    'textColor': 'textColor', 
    'backgroundColor': 'backgroundColor',
    'borderStyle': 'borderStyle',
    'borderColor': 'borderColor',
    'borderThickness': 'borderThickness',
    'startDate': 'startDate',
    'endDate': 'endDate',
    'linkEvidence': 'linkEvidence',
    'evidenceLabel': 'evidenceLabel',
    'evidenceDueDays': 'evidenceDueDays',
    'evidenceType': 'evidenceType',
    'klassensynchronisation': 'klassensynchronisation'
  };

  const actualProperty = propertyMapping[property] || property;
  const firstValue = tasks[0][actualProperty];
  
  return tasks.every(task => {
    const taskValue = task[actualProperty];
    
    // Spezielle Behandlung für Daten
    if (property === 'startDate' || property === 'endDate') {
      if (!firstValue && !taskValue) return true;
      if (!firstValue || !taskValue) return false;
      
      const firstDate = typeof firstValue === 'string' ? firstValue : formatDateForInput(firstValue);
      const taskDate = typeof taskValue === 'string' ? taskValue : formatDateForInput(taskValue);
      return firstDate === taskDate;
    }
    
    // Spezielle Behandlung für Farben
    if (property.includes('Color')) {
      const firstHex = rgbaToHex(firstValue);
      const taskHex = rgbaToHex(taskValue);
      return firstHex === taskHex;
    }
    
    // Spezielle Behandlung für Numbers (borderThickness, evidenceDueDays)
    if (property === 'borderThickness' || property === 'evidenceDueDays') {
      return Number(firstValue) === Number(taskValue);
    }
    
    return taskValue === firstValue;
  });
};

/**
 * Warning Icon Komponente für unterschiedliche Werte
 */
const DifferenceWarning = ({ show, tooltip }) => {
  if (!show) return null;
  
  return (
    <FiAlertTriangle 
      style={{ 
        color: '#ff9800', 
        marginLeft: '8px',
        cursor: 'help'
      }}
      title={tooltip}
      size={16}
    />
  );
};

/**
 * Dialog für das Bearbeiten von Eigenschaften mehrerer Tasks
 */
const BatchEditPropertiesDialog = ({ 
  show, 
  onClose, 
  selectedTasks = [],
  onConfirm 
}) => {
  // State für alle editierbaren Eigenschaften (Werte vom ersten Task)
  const [editTitle, setEditTitle] = useState('');
  const [editTextColor, setEditTextColor] = useState('#ffffff');
  const [editBackgroundColor, setEditBackgroundColor] = useState('#007bff');
  const [editBorderStyle, setEditBorderStyle] = useState('solid');
  const [editBorderColor, setEditBorderColor] = useState('#0056b3');
  const [editBorderThickness, setEditBorderThickness] = useState('1');
  const [editStartDate, setEditStartDate] = useState(null);
  const [editEndDate, setEditEndDate] = useState(null);
  const [editLinkEvidence, setEditLinkEvidence] = useState(false);
  const [editEvidenceLabel, setEditEvidenceLabel] = useState('');
  const [editEvidenceDueDays, setEditEvidenceDueDays] = useState('7');
  const [editEvidenceType, setEditEvidenceType] = useState('manual');
  const [editKlassensynchronisation, setEditKlassensynchronisation] = useState('');

  // State für verfolgte Änderungen (welche Felder wurden vom User geändert)
  const [changedFields, setChangedFields] = useState(new Set());

  // Initialisierung mit Werten vom ersten Task
  useEffect(() => {
    if (show && selectedTasks.length > 0) {
      const firstTask = selectedTasks[0];
      
      const initValues = {
        title: firstTask.label || '',
        textColor: rgbaToHex(firstTask.textColor || '#ffffff'),
        backgroundColor: rgbaToHex(firstTask.backgroundColor || '#007bff'),
        borderStyle: firstTask.borderStyle || 'solid',
        borderColor: rgbaToHex(firstTask.borderColor || '#0056b3'),
        borderThickness: String(firstTask.borderThickness || '1'),
        startDate: firstTask.startDate || null,
        endDate: firstTask.endDate || null,
        linkEvidence: firstTask.linkEvidence || false,
        evidenceLabel: firstTask.evidenceLabel || '',
        evidenceDueDays: String(firstTask.evidenceDueDays || '7'),
        evidenceType: firstTask.evidenceType || 'manual',
        klassensynchronisation: firstTask.klassensynchronisation || ''
      };

      setEditTitle(initValues.title);
      setEditTextColor(initValues.textColor);
      setEditBackgroundColor(initValues.backgroundColor);
      setEditBorderStyle(initValues.borderStyle);
      setEditBorderColor(initValues.borderColor);
      setEditBorderThickness(initValues.borderThickness);
      setEditStartDate(initValues.startDate);
      setEditEndDate(initValues.endDate);
      setEditLinkEvidence(initValues.linkEvidence);
      setEditEvidenceLabel(initValues.evidenceLabel);
      setEditEvidenceDueDays(initValues.evidenceDueDays);
      setEditEvidenceType(initValues.evidenceType);
      setEditKlassensynchronisation(initValues.klassensynchronisation);

      setChangedFields(new Set());
    }
  }, [show, selectedTasks]);

  // Handler für Feldänderungen
  const handleFieldChange = (fieldName, setValue) => (value) => {
    setValue(value);
    setChangedFields(prev => new Set([...prev, fieldName]));
  };

  const handleSave = () => {
    // Nur geänderte Eigenschaften sammeln
    const updates = {};
    
    if (changedFields.has('title')) updates.label = editTitle;
    if (changedFields.has('textColor')) updates.textColor = editTextColor;
    if (changedFields.has('backgroundColor')) updates.backgroundColor = editBackgroundColor;
    if (changedFields.has('borderStyle')) updates.borderStyle = editBorderStyle;
    if (changedFields.has('borderColor')) updates.borderColor = editBorderColor;
    if (changedFields.has('borderThickness')) updates.borderThickness = parseInt(editBorderThickness);
    if (changedFields.has('startDate')) updates.startDate = editStartDate;
    if (changedFields.has('endDate')) updates.endDate = editEndDate;
    if (changedFields.has('linkEvidence')) updates.linkEvidence = editLinkEvidence;
    if (changedFields.has('evidenceLabel')) updates.evidenceLabel = editEvidenceLabel;
    if (changedFields.has('evidenceDueDays')) updates.evidenceDueDays = parseInt(editEvidenceDueDays);
    if (changedFields.has('evidenceType')) updates.evidenceType = editEvidenceType;
    if (changedFields.has('klassensynchronisation')) updates.klassensynchronisation = editKlassensynchronisation;

    onConfirm({
      operation: 'edit-properties',
      updates,
      taskIds: selectedTasks.map(task => task.id)
    });
    
    onClose();
  };

  const sanitizeKlassensynchronisation = (value) => {
    if (!value) return '';
    return value
      .split(',')
      .map(klasse => klasse.trim())
      .filter(klasse => klasse.length > 0)
      .join(',');
  };

  if (!show) return null;

  return (
    <div 
      style={{ 
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 100000,
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: '1px solid #ddd',
        minWidth: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto'
      }}
      onClick={e => e.stopPropagation()}
    >
      <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
        Eigenschaften für {selectedTasks.length} Tasks bearbeiten
      </h3>
      
      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '14px', color: '#666' }}>
        <strong>Hinweis:</strong> Nur geänderte Eigenschaften werden auf alle ausgewählten Tasks angewendet.
        Unterschiedliche Werte zwischen Tasks werden mit <FiAlertTriangle style={{ color: '#ff9800' }} size={14} /> markiert.
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* Titel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Titel:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'label')} 
              tooltip="Verschiedene Titel in den ausgewählten Tasks"
            />
          </label>
          <input
            type="text"
            value={editTitle}
            onChange={e => handleFieldChange('title', setEditTitle)(e.target.value)}
            placeholder="Titel eingeben"
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              flex: 1, 
              minWidth: '200px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('title') ? '#4caf50' : '#ccc'}`,
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        
        {/* Textfarbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Textfarbe:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'textColor')} 
              tooltip="Verschiedene Textfarben in den ausgewählten Tasks"
            />
          </label>
          <input
            type="color"
            value={editTextColor}
            onChange={e => handleFieldChange('textColor', setEditTextColor)(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: `1px solid ${changedFields.has('textColor') ? '#4caf50' : '#ccc'}`, 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={editTextColor}
            onChange={e => handleFieldChange('textColor', setEditTextColor)(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('textColor') ? '#4caf50' : '#ccc'}`,
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#ffffff"
          />
        </div>
        
        {/* Hintergrundfarbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Hintergrund:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'backgroundColor')} 
              tooltip="Verschiedene Hintergrundfarben in den ausgewählten Tasks"
            />
          </label>
          <input
            type="color"
            value={editBackgroundColor}
            onChange={e => handleFieldChange('backgroundColor', setEditBackgroundColor)(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: `1px solid ${changedFields.has('backgroundColor') ? '#4caf50' : '#ccc'}`, 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={editBackgroundColor}
            onChange={e => handleFieldChange('backgroundColor', setEditBackgroundColor)(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('backgroundColor') ? '#4caf50' : '#ccc'}`,
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#007bff"
          />
        </div>
        
        {/* Rahmen-Stil */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Rahmen-Stil:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'borderStyle')} 
              tooltip="Verschiedene Rahmen-Stile in den ausgewählten Tasks"
            />
          </label>
          <select
            value={editBorderStyle}
            onChange={e => handleFieldChange('borderStyle', setEditBorderStyle)(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('borderStyle') ? '#4caf50' : '#ccc'}`,
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white',
              minWidth: '150px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <option value="solid">Durchgezogen</option>
            <option value="dashed">Gestrichelt</option>
            <option value="dotted">Gepunktet</option>
            <option value="double">Doppelt</option>
            <option value="groove">Eingeprägt</option>
            <option value="ridge">Erhaben</option>
            <option value="inset">Eingelassen</option>
            <option value="outset">Hervorgehoben</option>
            <option value="none">Kein Rahmen</option>
          </select>
        </div>
        
        {/* Rahmen-Farbe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Rahmen-Farbe:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'borderColor')} 
              tooltip="Verschiedene Rahmen-Farben in den ausgewählten Tasks"
            />
          </label>
          <input
            type="color"
            value={editBorderColor}
            onChange={e => handleFieldChange('borderColor', setEditBorderColor)(e.target.value)}
            style={{ 
              width: '50px', 
              height: '36px', 
              border: `1px solid ${changedFields.has('borderColor') ? '#4caf50' : '#ccc'}`, 
              borderRadius: '6px', 
              cursor: 'pointer',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="text"
            value={editBorderColor}
            onChange={e => handleFieldChange('borderColor', setEditBorderColor)(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '100px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('borderColor') ? '#4caf50' : '#ccc'}`,
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
            placeholder="#0056b3"
          />
        </div>
        
        {/* Rahmen-Dicke */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Rahmen-Dicke:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'borderThickness')} 
              tooltip="Verschiedene Rahmen-Dicken in den ausgewählten Tasks"
            />
          </label>
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={editBorderThickness}
            onChange={e => handleFieldChange('borderThickness', setEditBorderThickness)(e.target.value)}
            style={{ 
              flex: 1,
              minWidth: '100px',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
          <input
            type="number"
            min="0"
            max="20"
            value={editBorderThickness}
            onChange={e => handleFieldChange('borderThickness', setEditBorderThickness)(e.target.value)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              width: '60px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('borderThickness') ? '#4caf50' : '#ccc'}`,
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          />
          <span style={{ fontSize: '14px', color: '#666' }}>px</span>
        </div>
        
        {/* Startdatum */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Startdatum:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'startDate')} 
              tooltip="Verschiedene Startdaten in den ausgewählten Tasks"
            />
          </label>
          <input
            type="date"
            value={formatDateForInput(editStartDate)}
            onChange={e => handleFieldChange('startDate', setEditStartDate)(e.target.value ? new Date(e.target.value) : null)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('startDate') ? '#4caf50' : '#ccc'}`,
              outline: 'none',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        
        {/* Enddatum */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            Enddatum:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'endDate')} 
              tooltip="Verschiedene Enddaten in den ausgewählten Tasks"
            />
          </label>
          <input
            type="date"
            value={formatDateForInput(editEndDate)}
            onChange={e => handleFieldChange('endDate', setEditEndDate)(e.target.value ? new Date(e.target.value) : null)}
            style={{ 
              fontSize: '14px', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: `1px solid ${changedFields.has('endDate') ? '#4caf50' : '#ccc'}`,
              outline: 'none',
              cursor: 'pointer'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        
        {/* Nachweis verknüpfen */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
            <input
              type="checkbox"
              checked={editLinkEvidence}
              onChange={e => handleFieldChange('linkEvidence', setEditLinkEvidence)(e.target.checked)}
              style={{ 
                marginRight: '8px',
                cursor: 'pointer'
              }}
              onClick={e => e.stopPropagation()}
            />
            Nachweis verknüpfen
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'linkEvidence')} 
              tooltip="Verschiedene Nachweis-Einstellungen in den ausgewählten Tasks"
            />
          </label>
        </div>
        
        {/* Bezeichnung (nur wenn Checkbox aktiviert) */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
              Bezeichnung:
              <DifferenceWarning 
                show={!hasUniformValues(selectedTasks, 'evidenceLabel')} 
                tooltip="Verschiedene Nachweis-Bezeichnungen in den ausgewählten Tasks"
              />
            </label>
            <input
              type="text"
              value={editEvidenceLabel}
              onChange={e => handleFieldChange('evidenceLabel', setEditEvidenceLabel)(e.target.value)}
              placeholder="Bezeichnung des Nachweises"
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                flex: 1, 
                minWidth: '200px', 
                borderRadius: '6px', 
                border: `1px solid ${changedFields.has('evidenceLabel') ? '#4caf50' : '#ccc'}`,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
        
        {/* Fällig nach x Tagen */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
              Fällig nach:
              <DifferenceWarning 
                show={!hasUniformValues(selectedTasks, 'evidenceDueDays')} 
                tooltip="Verschiedene Fälligkeits-Tage in den ausgewählten Tasks"
              />
            </label>
            <input
              type="number"
              min="1"
              value={editEvidenceDueDays}
              onChange={e => handleFieldChange('evidenceDueDays', setEditEvidenceDueDays)(e.target.value)}
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                width: '80px', 
                borderRadius: '6px', 
                border: `1px solid ${changedFields.has('evidenceDueDays') ? '#4caf50' : '#ccc'}`,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onClick={e => e.stopPropagation()}
            />
            <span style={{ fontSize: '14px', color: '#666' }}>Tagen ab Beginn</span>
          </div>
        )}
        
        {/* Nachweistyp */}
        {editLinkEvidence && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>
              Nachweistyp:
              <DifferenceWarning 
                show={!hasUniformValues(selectedTasks, 'evidenceType')} 
                tooltip="Verschiedene Nachweis-Typen in den ausgewählten Tasks"
              />
            </label>
            <select
              value={editEvidenceType}
              onChange={e => handleFieldChange('evidenceType', setEditEvidenceType)(e.target.value)}
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: `1px solid ${changedFields.has('evidenceType') ? '#4caf50' : '#ccc'}`,
                outline: 'none',
                cursor: 'pointer',
                backgroundColor: 'white',
                minWidth: '200px',
                flex: 1
              }}
              onClick={e => e.stopPropagation()}
            >
              <option value="manual">Manuell</option>
              <option value="moodle-badge">Moodle Badge</option>
              <option value="moodle-activity">Moodle Aktivität</option>
              <option value="simsys-exam">Simsys Prüfung</option>
            </select>
          </div>
        )}

        {/* Klassensynchronisation */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ minWidth: '140px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left', paddingTop: '8px' }}>
            Klassensynchronisation:
            <DifferenceWarning 
              show={!hasUniformValues(selectedTasks, 'klassensynchronisation')} 
              tooltip="Verschiedene Klassensynchronisations-Einstellungen in den ausgewählten Tasks"
            />
          </label>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <input
              type="text"
              value={editKlassensynchronisation || ''}
              onChange={e => handleFieldChange('klassensynchronisation', setEditKlassensynchronisation)(e.target.value)}
              onBlur={e => {
                e.target.style.borderColor = changedFields.has('klassensynchronisation') ? '#4caf50' : '#ccc';
                setEditKlassensynchronisation(sanitizeKlassensynchronisation(e.target.value));
              }}
              placeholder="Klassen kommasepariert (z.B. IT21a, IT21b, IN21a)"
              style={{ 
                fontSize: '14px', 
                padding: '8px 12px', 
                width: '100%', 
                borderRadius: '6px', 
                border: `1px solid ${changedFields.has('klassensynchronisation') ? '#4caf50' : '#ccc'}`,
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onClick={e => e.stopPropagation()}
            />
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.3'
            }}>
              Geben Sie Klassennamen für die Benutzersynchronisation ein. Mehrere Klassen durch Kommas trennen.
            </div>
          </div>
        </div>
      </div>
      
      {/* Zusammenfassung der Änderungen */}
      {changedFields.size > 0 && (
        <div style={{ 
          marginTop: '20px', 
          padding: '12px', 
          backgroundColor: '#e7f3ff', 
          borderRadius: '6px',
          border: '1px solid #007bff'
        }}>
          <strong style={{ color: '#007bff' }}>Geplante Änderungen:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#0056b3' }}>
            {Array.from(changedFields).map(field => (
              <li key={field} style={{ fontSize: '14px', marginBottom: '4px' }}>
                {field === 'title' && 'Titel'}
                {field === 'textColor' && 'Textfarbe'}
                {field === 'backgroundColor' && 'Hintergrundfarbe'}
                {field === 'borderStyle' && 'Rahmen-Stil'}
                {field === 'borderColor' && 'Rahmen-Farbe'}
                {field === 'borderThickness' && 'Rahmen-Dicke'}
                {field === 'startDate' && 'Startdatum'}
                {field === 'endDate' && 'Enddatum'}
                {field === 'linkEvidence' && 'Nachweis-Verknüpfung'}
                {field === 'evidenceLabel' && 'Nachweis-Bezeichnung'}
                {field === 'evidenceDueDays' && 'Nachweis-Fälligkeit'}
                {field === 'evidenceType' && 'Nachweis-Typ'}
                {field === 'klassensynchronisation' && 'Klassensynchronisation'}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#4caf50',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s',
            opacity: changedFields.size === 0 ? 0.5 : 1
          }}
          onClick={handleSave}
          disabled={changedFields.size === 0}
          onMouseEnter={e => {
            if (changedFields.size > 0) {
              e.target.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (changedFields.size > 0) {
              e.target.style.backgroundColor = '#4caf50';
            }
          }}
          title={changedFields.size === 0 ? "Keine Änderungen vorgenommen" : "Änderungen auf alle ausgewählten Tasks anwenden"}
        >
          <FiCheck />
          Änderungen anwenden ({changedFields.size})
        </button>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#f44336',
            border: 'none',
            padding: '10px 16px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={onClose}
          onMouseEnter={e => e.target.style.backgroundColor = '#da190b'}
          onMouseLeave={e => e.target.style.backgroundColor = '#f44336'}
          title="Abbrechen ohne Änderungen"
        >
          <FiX />
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default BatchEditPropertiesDialog;